import type { PortIndicator } from '../../Port/utils';
import { colors, darkThemeSelector, styled } from '../../../stitches.config';
import { chassisVars } from '../../Chassis/atoms/utils';
import { SlotIndicatorIcon } from './SlotIndicators';
import { slotVars } from './utils';

const SlotCaptionContainer = styled('div', {
  zIndex: slotVars.zIndex.caption,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  widthAll: `$${chassisVars.slot.width}`,
  heightAll: `$${chassisVars.slot.caption}`,
  paddingLeft: '$1',
});
SlotCaptionContainer.displayName = 'SlotCaptionContainer';

/**
 * The number chip, right-aligned so its last digit lands on the same column in every slot whether or
 * not anything sits beside it.
 *
 * `flex: 1` is what pins that edge: the chip takes whatever the icons leave, so its right edge is the
 * caption inset either way and only its left one moves. A label too wide for the room left over
 * overflows leftward, under those icons, rather than growing the slot and shunting its neighbours off
 * their column.
 */
const SlotCaptionNumber = styled('span', {
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'flex-end',
  minWidth: 0,
  paddingRight: `$${chassisVars.slot.captionInset}`,
  color: colors.bodyNeutralLight,
  fontFamily: '$mono',
  fontSize: '$9',
  lineHeight: `$${chassisVars.slot.caption}`,
  whiteSpace: 'nowrap',

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },

  variants: {
    selected: {
      true: {
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          color: colors.bodyBrandDark,
        },
      },
      false: {},
    },
  },
});
SlotCaptionNumber.displayName = 'SlotCaptionNumber';

export type SlotCaptionProps = {
  /**
   * The slot's indicators, from `getPortIndicators`. The ones placed in the caption — routing and
   * SPAN, or a pip for each — are drawn to the left of the number; the rest belong to
   * `SlotIndicators` and are ignored here.
   */
  indicators?: PortIndicator[];
  /** What the chip reads: a port number, or a name for faceplates that use one ('WAN0', 'OOB'). */
  number?: string | number | null;
  selected?: boolean;
};

/**
 * The row under a slot's glyph: the number chip, and the properties that read better beside it
 * than out on the rail.
 *
 * PoE and uplink keep the rail to themselves, so what sits here is what the port is *doing* —
 * configured or temporary states, which can change while you're looking at it.
 */
export function SlotCaption({ indicators = [], number, selected }: SlotCaptionProps) {
  return (
    <SlotCaptionContainer>
      {indicators
        .filter((indicator) => indicator.placement === 'caption')
        .map((indicator) => (
          <SlotIndicatorIcon key={indicator.id} indicator={indicator} />
        ))}
      <SlotCaptionNumber selected={selected}>{number}</SlotCaptionNumber>
    </SlotCaptionContainer>
  );
}
